/**
 * WelcomeDisclaimer.jsx
 * 
 * Kombinierter Willkommens-Dialog für neue Kunden
 * Vereint den FirstTimeDisclaimer und LegalAcceptanceModal in einem Dialog
 * 
 * Zeigt:
 * - Willkommensnachricht
 * - Medizinische Hinweise (Nutzungszweck & Wichtige Hinweise)
 * - AGB & Datenschutz Links (aus Admin-Settings)
 * - Benutzerdefinierte Bedingungen (aus Admin-Settings)
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Shield, Info, AlertTriangle, FileText, 
  ExternalLink, Loader2, CheckSquare 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WelcomeDisclaimer = ({ open, onAccepted, onDecline, token, studioName = 'Studio' }) => {
  const { t } = useTranslation();
  // Medical disclaimer checkboxes
  const [acceptedMedical, setAcceptedMedical] = useState(false);
  
  // Legal checkboxes
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [customAcceptances, setCustomAcceptances] = useState({});
  
  const [loading, setLoading] = useState(false);
  const [legalInfo, setLegalInfo] = useState({ 
    agb_url: '', 
    privacy_url: '',
    custom_conditions: []
  });

  useEffect(() => {
    if (open) {
      loadLegalInfo();
      // Reset all acceptances when modal opens
      setAcceptedMedical(false);
      setAcceptedTerms(false);
      setAcceptedPrivacy(false);
      setCustomAcceptances({});
    }
  }, [open]);

  const loadLegalInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/legal/public`);
      setLegalInfo({
        agb_url: response.data.agb_url || '',
        privacy_url: response.data.privacy_url || '',
        custom_conditions: response.data.custom_conditions || []
      });
    } catch (error) {
      console.error('Error loading legal info:', error);
    }
  };

  const toggleCustomAcceptance = (conditionId) => {
    setCustomAcceptances(prev => ({
      ...prev,
      [conditionId]: !prev[conditionId]
    }));
  };

  const allRequiredAccepted = () => {
    // Medical disclaimer must be accepted
    if (!acceptedMedical) return false;
    
    // Terms and privacy must be accepted
    if (!acceptedTerms || !acceptedPrivacy) return false;
    
    // Check all required custom conditions
    for (const condition of legalInfo.custom_conditions) {
      if (condition.required && !customAcceptances[condition.id]) {
        return false;
      }
    }
    return true;
  };

  const handleAccept = async () => {
    if (!allRequiredAccepted()) {
      toast.error(t('portal.onboarding.accept_all_required'));
      return;
    }

    setLoading(true);
    try {
      // Save to localStorage
      const now = new Date().toISOString();
      localStorage.setItem('terms_accepted', now);

      // Collect all accepted custom condition IDs
      const acceptedCustomIds = Object.keys(customAcceptances).filter(id => customAcceptances[id]);
      
      // Save to server - use portal endpoint which handles all acceptances
      if (token) {
        // Save legal acceptance with custom conditions (this endpoint handles everything)
        await axios.post(
          `${API}/portal/auth/accept-legal`,
          { 
            accepted_custom_conditions: acceptedCustomIds,
            medical_disclaimer: acceptedMedical  // Include medical disclaimer acceptance
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success(t('portal.onboarding.thanks_welcome', { studio: studioName }));
      onAccepted?.();
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast.error(t('portal.onboarding.save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    onDecline?.();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg bg-zinc-900 border-zinc-700 max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="welcome-disclaimer"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-600/20 rounded-full">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <DialogTitle className="text-xl text-white">
              {t('portal.onboarding.welcome_title', { studio: studioName })}
            </DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="text-left space-y-4 pt-2">
              <p className="text-zinc-300">
                {t('portal.onboarding.welcome_intro')}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* === SECTION 1: Medical Disclaimers === */}
          <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-blue-300 flex items-center gap-2">
              <Info className="w-4 h-4" />
              {t('portal.onboarding.purpose_title')}
            </h4>
            <p className="text-sm text-blue-200">
              <Trans i18nKey="portal.onboarding.purpose_text" components={{ b: <strong className="text-white" /> }} />
            </p>
          </div>
          
          <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-amber-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('portal.onboarding.important_title')}
            </h4>
            <ul className="text-sm text-amber-200 list-disc list-inside space-y-1">
              <li>{t('portal.onboarding.important_1')}</li>
              <li>{t('portal.onboarding.important_2')}</li>
              <li>{t('portal.onboarding.important_3')}</li>
              <li>{t('portal.onboarding.important_4')}</li>
            </ul>
          </div>

          {/* Medical Disclaimer Checkbox */}
          <div className="flex items-start space-x-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <Checkbox
              id="medical"
              checked={acceptedMedical}
              onCheckedChange={setAcceptedMedical}
              className="mt-1 border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              data-testid="accept-medical-checkbox"
            />
            <label htmlFor="medical" className="text-sm cursor-pointer text-zinc-300">
              {t('portal.onboarding.medical_checkbox')}
            </label>
          </div>

          {/* === SECTION 2: Legal Documents === */}
          <div className="border-t border-zinc-700 pt-4 mt-4">
            <p className="text-xs text-zinc-500 mb-3 font-medium">{t('portal.onboarding.legal_docs')}</p>
          </div>

          {/* AGB Checkbox */}
          <div className="flex items-start space-x-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <Checkbox
              id="terms"
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
              className="mt-1 border-zinc-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
              data-testid="accept-terms-checkbox"
            />
            <div className="flex-1">
              <label
                htmlFor="terms"
                className="text-sm font-medium text-white cursor-pointer flex items-center gap-2"
              >
                <FileText className="w-4 h-4 text-yellow-500" />
                {t('portal.onboarding.terms_title')}
              </label>
              <p className="text-xs text-zinc-500 mt-1">
                {t('portal.onboarding.terms_desc')}
              </p>
              {legalInfo.agb_url && (
                <a
                  href={legalInfo.agb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-yellow-500 hover:text-yellow-400 inline-flex items-center gap-1 mt-2"
                >
                  {t('portal.onboarding.open_terms')} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Privacy Checkbox */}
          <div className="flex items-start space-x-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <Checkbox
              id="privacy"
              checked={acceptedPrivacy}
              onCheckedChange={setAcceptedPrivacy}
              className="mt-1 border-zinc-600 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
              data-testid="accept-privacy-checkbox"
            />
            <div className="flex-1">
              <label
                htmlFor="privacy"
                className="text-sm font-medium text-white cursor-pointer flex items-center gap-2"
              >
                <Shield className="w-4 h-4 text-blue-400" />
                {t('portal.onboarding.privacy_title')}
              </label>
              <p className="text-xs text-zinc-500 mt-1">
                {t('portal.onboarding.privacy_desc')}
              </p>
              {legalInfo.privacy_url && (
                <a
                  href={legalInfo.privacy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 mt-2"
                >
                  {t('portal.onboarding.open_privacy')} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Custom Conditions (from Admin Settings) */}
          {legalInfo.custom_conditions.length > 0 && (
            <>
              <div className="border-t border-zinc-700 pt-4 mt-4">
                <p className="text-xs text-zinc-500 mb-3 font-medium">{t('portal.onboarding.extra_conditions')}</p>
              </div>
              
              {legalInfo.custom_conditions.map((condition) => (
                <div 
                  key={condition.id}
                  className="flex items-start space-x-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700"
                >
                  <Checkbox
                    id={`condition-${condition.id}`}
                    checked={customAcceptances[condition.id] || false}
                    onCheckedChange={() => toggleCustomAcceptance(condition.id)}
                    className="mt-1 border-zinc-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`condition-${condition.id}`}
                      className="text-sm font-medium text-white cursor-pointer flex items-center gap-2"
                    >
                      <CheckSquare className="w-4 h-4 text-green-400" />
                      {condition.title} {condition.required && '*'}
                    </label>
                    {condition.description && (
                      <p className="text-xs text-zinc-500 mt-1">
                        {condition.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Required note */}
          <p className="text-xs text-zinc-500 text-center mt-2">
            {t('portal.onboarding.required_note')}
          </p>
        </div>

        <DialogFooter className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={handleDecline} 
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
          >
            {t('portal.onboarding.decline')}
          </Button>
          <Button 
            onClick={handleAccept}
            disabled={!allRequiredAccepted() || loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-zinc-700 disabled:text-zinc-500"
            data-testid="accept-all-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('portal.onboarding.saving')}
              </>
            ) : (
              t('portal.onboarding.accept_start')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeDisclaimer;
